import {
  Inject,
  Injectable,
  InjectionToken,
  OnDestroy,
  Optional,
} from '@angular/core';
import { Observable, Subject, Subscription, defer, of, throwError } from 'rxjs';
import {
  finalize,
  map,
  shareReplay,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { io, Socket } from 'socket.io-client';
import { environment } from 'src/environments/environment';
import { AceAuthService } from '../services/auth/ace-auth.service';
import {
  ChatMessageDto,
  ChatReadResponse,
  SendChatMessageDto,
  SupportWorkflowDto,
} from './chat.dto';

export interface ChatMessageCreatedEvent {
  chatId: string;
  message: ChatMessageDto;
}

export interface ChatUpdatedEvent {
  chatId: string;
  message: ChatMessageDto | null;
}

export interface ChatMessageReadEvent {
  chatId: string;
  userId: string;
  messageId: string;
}

export interface ChatTypingEvent {
  chatId: string;
  userId: string;
  isTyping: boolean;
}

export interface ChatMemberActivityEvent {
  chatId: string;
  userId: string;
  lastActiveAt: string;
}

export interface ChatAuditEvent {
  chatId: string;
  message?: ChatMessageDto | null;
}

export interface SupportWorkflowUpdatedEvent {
  chatId: string;
  workflow: SupportWorkflowDto;
}

export interface ChatSocketAck<T> {
  event: string;
  data: T;
}

export interface ChatRoomAck {
  chatId: string;
}

export interface ChatRealtimeConnectionEvent {
  connected: boolean;
  reconnected: boolean;
}

type ChatSocketEnvironment = typeof environment & {
  chatSocketUrl?: string;
  teamSocketUrl?: string;
  teamRealtimeUrl?: string;
};

/**
 * Optional explicit URL keeps environment-specific socket configuration out
 * of the REST endpoint. The fallback is only for existing environments that
 * have not added that setting yet.
 */
export function resolveChatSocketUrl(): string | null {
  const config = environment as ChatSocketEnvironment;
  const configuredUrl =
    config.chatSocketUrl?.trim() ||
    config.teamSocketUrl?.trim() ||
    config.teamRealtimeUrl?.trim();

  if (configuredUrl) {
    return stripTrailingSlash(configuredUrl);
  }

  const teamApiUrl = config.teamApiUrl?.trim();
  if (!teamApiUrl) {
    return null;
  }

  try {
    const url = new URL(teamApiUrl);
    url.pathname = url.pathname.replace(/\/api\/v\d+\/?$/i, '');
    url.search = '';
    url.hash = '';
    return stripTrailingSlash(url.toString());
  } catch {
    return null;
  }
}

export function chatNamespaceUrl(baseUrl: string): string {
  const normalized = stripTrailingSlash(baseUrl);
  return normalized.endsWith('/chat') ? normalized : `${normalized}/chat`;
}

export const CHAT_SOCKET_URL = new InjectionToken<string | null>(
  'CHAT_SOCKET_URL',
  {
    providedIn: 'root',
    factory: resolveChatSocketUrl,
  }
);

const ACK_TIMEOUT_MS = 5000;

@Injectable({ providedIn: 'root' })
export class ChatRealtimeService implements OnDestroy {
  private socket: Socket | null = null;
  private socketToken: string | null = null;
  private socketCleanup: (() => void) | null = null;
  private refreshSubscription: Subscription | null = null;
  private connectRequest: Observable<Socket> | null = null;
  private connectionGeneration = 0;
  private manuallyDisconnected = false;
  private destroyed = false;
  private hasConnected = false;
  private reconnectPending = false;
  private reconnectAttempt = 0;
  private reconnectTimer: number | null = null;
  private connectRequestToken: object | null = null;
  /**
   * Keep one room membership per consumer. The notification service and the
   * chat page share this singleton, so a page teardown must not unsubscribe
   * the global notification listener from rooms it still needs.
   */
  private readonly joinedChatRefCounts = new Map<string, number>();
  private readonly seenMessageIds = new Set<string>();

  private readonly messageCreatedSubject =
    new Subject<ChatMessageCreatedEvent>();
  private readonly chatUpdatedSubject = new Subject<ChatUpdatedEvent>();
  private readonly messageReadSubject = new Subject<ChatMessageReadEvent>();
  private readonly chatTypingSubject = new Subject<ChatTypingEvent>();
  private readonly memberActivitySubject =
    new Subject<ChatMemberActivityEvent>();
  private readonly chatAuditSubject = new Subject<ChatAuditEvent>();
  private readonly supportWorkflowUpdatedSubject =
    new Subject<SupportWorkflowUpdatedEvent>();
  private readonly connectionSubject =
    new Subject<ChatRealtimeConnectionEvent>();
  private readonly reconnectedSubject = new Subject<void>();
  private readonly connectCancellation = new Subject<void>();

  readonly messageCreated$ = this.messageCreatedSubject.asObservable();
  readonly chatUpdated$ = this.chatUpdatedSubject.asObservable();
  readonly messageRead$ = this.messageReadSubject.asObservable();
  readonly chatTyping$ = this.chatTypingSubject.asObservable();
  readonly chatMemberActivity$ = this.memberActivitySubject.asObservable();
  readonly memberActivity$ = this.chatMemberActivity$;
  readonly chatAudit$ = this.chatAuditSubject.asObservable();
  readonly supportWorkflowUpdated$ =
    this.supportWorkflowUpdatedSubject.asObservable();
  readonly connection$ = this.connectionSubject.asObservable();
  readonly reconnected$ = this.reconnectedSubject.asObservable();

  constructor(
    private readonly aceAuthService: AceAuthService,
    @Optional() @Inject(CHAT_SOCKET_URL) socketUrl?: string | null
  ) {
    this.socketUrl =
      socketUrl === undefined ? resolveChatSocketUrl() : socketUrl;
  }

  private readonly socketUrl: string | null;

  ngOnDestroy(): void {
    this.destroyed = true;
    this.disconnect();
    this.messageCreatedSubject.complete();
    this.chatUpdatedSubject.complete();
    this.messageReadSubject.complete();
    this.chatTypingSubject.complete();
    this.memberActivitySubject.complete();
    this.chatAuditSubject.complete();
    this.supportWorkflowUpdatedSubject.complete();
    this.connectionSubject.complete();
    this.reconnectedSubject.complete();
    this.connectCancellation.complete();
    this.seenMessageIds.clear();
  }

  connect(): Observable<Socket> {
    if (this.destroyed) {
      return throwError(() => new Error('Chat realtime service is destroyed'));
    }
    if (!this.socketUrl) {
      return throwError(() => new Error('Chat realtime is disabled'));
    }

    this.manuallyDisconnected = false;
    if (!this.connectRequest) {
      const generation = this.connectionGeneration;
      const requestToken = {};
      const request = this.aceAuthService.getToken().pipe(
        switchMap(token =>
          generation === this.connectionGeneration && !this.manuallyDisconnected
            ? this.openSocket(token)
            : throwError(() => new Error('Chat realtime connection cancelled'))
        ),
        takeUntil(this.connectCancellation),
        finalize(() => {
          if (this.connectRequestToken === requestToken) {
            this.connectRequest = null;
            this.connectRequestToken = null;
          }
        }),
        shareReplay({ bufferSize: 1, refCount: true })
      );
      this.connectRequest = request;
      this.connectRequestToken = requestToken;
    }
    return this.connectRequest;
  }

  disconnect(): void {
    this.manuallyDisconnected = true;
    this.connectionGeneration++;
    this.connectCancellation.next();
    this.connectRequest = null;
    this.connectRequestToken = null;
    this.reconnectPending = false;
    this.reconnectAttempt = 0;
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.joinedChatRefCounts.clear();
    this.refreshSubscription?.unsubscribe();
    this.refreshSubscription = null;
    this.disposeSocket();
  }

  join(chatId: string): Observable<ChatRoomAck> {
    return defer(() => {
      this.retainChat(chatId);
      let acknowledged = false;
      return this.emitAck<ChatRoomAck>('joinChat', { chatId }).pipe(
        map(response => unwrapAck(response)),
        tap(() => (acknowledged = true)),
        finalize(() => {
          if (!acknowledged) this.releaseChat(chatId);
        })
      );
    });
  }

  leave(chatId: string): Observable<ChatRoomAck> {
    return defer(() => {
      const shouldLeaveRoom = this.releaseChat(chatId);
      return shouldLeaveRoom
        ? this.emitAck<ChatRoomAck>('leaveChat', { chatId })
        : of({ chatId });
    }).pipe(map(response => unwrapAck(response)));
  }

  joinChat(chatId: string): Observable<ChatRoomAck> {
    return this.join(chatId);
  }

  leaveChat(chatId: string): Observable<ChatRoomAck> {
    return this.leave(chatId);
  }

  sendMessage(
    chatId: string,
    message: string | Pick<SendChatMessageDto, 'body' | 'replyToMessageId'>
  ): Observable<ChatMessageDto> {
    const payload =
      typeof message === 'string'
        ? { chatId, body: message }
        : { chatId, ...message };

    return this.emitAck<ChatMessageDto>('sendMessage', payload).pipe(
      map(response => unwrapAck(response))
    );
  }

  markRead(chatId: string, messageId: string): Observable<ChatReadResponse> {
    return this.emitAck<ChatReadResponse>('markRead', {
      chatId,
      messageId,
    }).pipe(map(response => unwrapAck(response)));
  }

  /** Typing is intentionally fire-and-forget; the gateway throttles it. */
  typing(chatId: string, isTyping = true): void {
    if (!this.socket?.connected || !chatId) {
      return;
    }
    this.socket.emit('typing', { chatId, isTyping });
  }

  sendTyping(chatId: string, isTyping = true): void {
    this.typing(chatId, isTyping);
  }

  private openSocket(token: string): Observable<Socket> {
    const socket = this.ensureSocket(token);

    return new Observable<Socket>(observer => {
      if (socket.connected) {
        observer.next(socket);
        observer.complete();
        return undefined;
      }

      const onConnect = () => {
        observer.next(socket);
        observer.complete();
      };
      const onConnectError = (error: Error) => observer.error(error);

      socket.once('connect', onConnect);
      socket.once('connect_error', onConnectError);
      socket.connect();

      return () => {
        socket.off('connect', onConnect);
        socket.off('connect_error', onConnectError);
      };
    });
  }

  private ensureSocket(token: string): Socket {
    if (this.socket && this.socketToken === token) {
      this.socket.auth = { token };
      return this.socket;
    }

    this.disposeSocket();
    const socket = io(chatNamespaceUrl(this.socketUrl as string), {
      autoConnect: false,
      auth: { token },
      forceNew: true,
      reconnection: false,
      transports: ['websocket'],
    });

    this.socket = socket;
    this.socketToken = token;
    this.bindSocketEvents(socket);
    return socket;
  }

  private bindSocketEvents(socket: Socket): void {
    const onConnect = () => {
      const reconnected = this.hasConnected || this.reconnectPending;
      this.hasConnected = true;
      this.reconnectPending = false;
      this.reconnectAttempt = 0;
      this.rejoinChats(socket);
      this.connectionSubject.next({ connected: true, reconnected });
      if (reconnected) this.reconnectedSubject.next();
    };
    const onDisconnect = () => {
      if (this.socket === socket && !this.manuallyDisconnected) {
        this.reconnectPending = true;
        this.connectionSubject.next({ connected: false, reconnected: false });
        this.refreshAndReconnect(socket);
      }
    };
    const onConnectError = () => {
      if (this.socket === socket && !this.manuallyDisconnected) {
        this.reconnectPending = true;
        this.refreshAndReconnect(socket);
      }
    };
    const onMessageCreated = (payload: ChatMessageCreatedEvent) =>
      this.publishMessageCreated(payload);
    const onChatUpdated = (payload: ChatUpdatedEvent) =>
      this.chatUpdatedSubject.next(payload);
    const onMessageRead = (payload: ChatMessageReadEvent) =>
      this.messageReadSubject.next(payload);
    const onChatTyping = (payload: ChatTypingEvent) =>
      this.chatTypingSubject.next(payload);
    const onMemberActivity = (payload: ChatMemberActivityEvent) =>
      this.memberActivitySubject.next(payload);
    const onChatAudit = (payload: ChatAuditEvent) => {
      if (!payload?.chatId) return;
      if (
        payload.message &&
        !this.rememberMessage(payload.chatId, payload.message)
      ) {
        return;
      }
      this.chatAuditSubject.next(payload);
    };
    const onSupportWorkflowUpdated = (payload: SupportWorkflowUpdatedEvent) =>
      this.supportWorkflowUpdatedSubject.next(payload);

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('connect_error', onConnectError);
    socket.on('message.created', onMessageCreated);
    socket.on('chat.updated', onChatUpdated);
    socket.on('message.read', onMessageRead);
    socket.on('chat.typing', onChatTyping);
    socket.on('chat.member.activity', onMemberActivity);
    socket.on('chat.audit', onChatAudit);
    socket.on('support.workflow.updated', onSupportWorkflowUpdated);

    this.socketCleanup = () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('connect_error', onConnectError);
      socket.off('message.created', onMessageCreated);
      socket.off('chat.updated', onChatUpdated);
      socket.off('message.read', onMessageRead);
      socket.off('chat.typing', onChatTyping);
      socket.off('chat.member.activity', onMemberActivity);
      socket.off('chat.audit', onChatAudit);
      socket.off('support.workflow.updated', onSupportWorkflowUpdated);
    };
  }

  private rejoinChats(socket: Socket): void {
    for (const chatId of this.joinedChatRefCounts.keys()) {
      socket.emit('joinChat', { chatId });
    }
  }

  private publishMessageCreated(payload: ChatMessageCreatedEvent): void {
    if (
      !payload?.chatId ||
      !payload.message?.id ||
      payload.message.chatId !== payload.chatId ||
      !this.rememberMessage(payload.chatId, payload.message)
    ) {
      return;
    }
    this.messageCreatedSubject.next(payload);
  }

  private rememberMessage(chatId: string, message: ChatMessageDto): boolean {
    const key = `${chatId}:${message.id}`;
    if (this.seenMessageIds.has(key)) return false;
    this.seenMessageIds.add(key);
    if (this.seenMessageIds.size > 200) {
      const oldest = this.seenMessageIds.values().next().value;
      if (oldest) this.seenMessageIds.delete(oldest);
    }
    return true;
  }

  private retainChat(chatId: string): void {
    const references = this.joinedChatRefCounts.get(chatId) ?? 0;
    this.joinedChatRefCounts.set(chatId, references + 1);
  }

  private releaseChat(chatId: string): boolean {
    const references = this.joinedChatRefCounts.get(chatId) ?? 0;
    if (references <= 1) {
      this.joinedChatRefCounts.delete(chatId);
      return references === 1;
    }
    this.joinedChatRefCounts.set(chatId, references - 1);
    return false;
  }

  private refreshAndReconnect(socket: Socket): void {
    if (this.refreshSubscription && !this.refreshSubscription.closed) {
      return;
    }
    if (this.reconnectTimer !== null) return;

    const delay = Math.min(
      8000,
      250 * 2 ** Math.min(this.reconnectAttempt++, 5)
    );
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.refreshAndReconnectNow(socket);
    }, delay);
  }

  private refreshAndReconnectNow(socket: Socket): void {
    if (this.destroyed || this.manuallyDisconnected || this.socket !== socket) {
      return;
    }

    this.aceAuthService.clear();
    const refresh$ = this.aceAuthService.getToken().pipe(
      tap(token => {
        if (
          this.destroyed ||
          this.manuallyDisconnected ||
          this.socket !== socket
        ) {
          return;
        }
        this.socketToken = token;
        socket.auth = { token };
        socket.connect();
      }),
      finalize(() => (this.refreshSubscription = null))
    );
    const subscription = refresh$.subscribe({ error: () => undefined });
    this.refreshSubscription = subscription;
    if (subscription.closed) {
      this.refreshSubscription = null;
    }
  }

  private emitAck<T>(
    event: string,
    payload: object
  ): Observable<ChatSocketAck<T>> {
    return this.connect().pipe(
      switchMap(
        socket =>
          new Observable<ChatSocketAck<T>>(observer => {
            let settled = false;
            socket
              .timeout(ACK_TIMEOUT_MS)
              .emit(
                event,
                payload,
                (error: Error | null, response: ChatSocketAck<T>) => {
                  if (settled) return;
                  settled = true;
                  if (error) {
                    observer.error(error);
                    return;
                  }
                  observer.next(response);
                  observer.complete();
                }
              );
            return () => {
              settled = true;
            };
          })
      )
    );
  }

  private disposeSocket(): void {
    const socket = this.socket;
    this.socket = null;
    this.socketToken = null;
    this.hasConnected = false;
    this.reconnectPending = false;
    this.socketCleanup?.();
    this.socketCleanup = null;
    socket?.disconnect();
  }
}

function unwrapAck<T>(response: ChatSocketAck<T> | T): T {
  if (
    response &&
    typeof response === 'object' &&
    'event' in response &&
    'data' in response
  ) {
    return (response as ChatSocketAck<T>).data;
  }
  return response as T;
}

function stripTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '');
}
